import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { db, storage } from '../../firebase';
import { getSurahAyahsFromFirestore, updateSurahMeta, updateAyah } from '../../services/quranService';
import AyahEditDialog from './AyahEditDialog';
import ImageUploadBox from '../../components/ImageUploadBox';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, Box, CircularProgress, Snackbar, TextField, Grid } from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import EditIcon from '@material-ui/icons/Edit';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles((theme) => ({
  headerCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: '#f8fafc'
  },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}` },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400 },
  textField: {
    '& .MuiInputBase-root': {
      color: '#f8fafc' },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#334155' }
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary } },
  uploadBox: {
    border: '2px dashed rgba(148,163,184,0.3)',
    borderRadius: 12,
    padding: '24px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: theme.palette.type === "dark" ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.4)",
    '&:hover': { borderColor: '#10b981', background: 'rgba(16,185,129,0.05)' } },
  previewBox: { position: 'relative', display: 'inline-block', borderRadius: 12, overflow: 'hidden' },
  previewImg: { maxHeight: 160, borderRadius: 12, display: 'block' },
  removeBtn: { position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', width: 28, height: 28, '&:hover': { background: 'rgba(239,68,68,0.8)' } } }));

export default function SurahDetailPage() {
  const classes = useStyles();
  
  const theme = useTheme();
const { id } = useParams();
  const history = useHistory();
  
  const [surah, setSurah] = useState(null);
  const [ayahs, setAyahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [editAyahOpen, setEditAyahOpen] = useState(false);
  const [selectedAyah, setSelectedAyah] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const surahDoc = await db.collection('surahs').doc(id).get();
      if (surahDoc.exists) {
        setSurah(surahDoc.data());
      }
      
      const ayahDocs = await getSurahAyahsFromFirestore(id);
      setAyahs(ayahDocs);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load details' });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMetaChange = (field, value) => {
    setSurah({ ...surah, [field]: value });
  };

  const saveSurahMeta = async () => {
    setSavingMeta(true);
    try {
      await updateSurahMeta(id, {
        surahImage: surah.surahImage || '',
        transliterationBn: surah.transliterationBn || '',
        transliterationEn: surah.transliterationEn || ''
      });
      setSnackbar({ open: true, message: 'Surah metadata saved!' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save metadata' });
    }
    setSavingMeta(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const ref = storage.ref(`surah_images/${id}`);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      setSurah({ ...surah, surahImage: url });
      setSnackbar({ open: true, message: 'Image uploaded' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Image upload failed' });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadingImage(false);
  };

  const handleRemoveImage = () => {
    setSurah({ ...surah, surahImage: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEditAyah = (ayah) => {
    setSelectedAyah(ayah);
    setEditAyahOpen(true);
  };

  const handleSaveAyah = async (updatedAyah) => {
    setEditAyahOpen(false);
    try {
      await updateAyah(id, updatedAyah.numberInSurah, updatedAyah);
      setSnackbar({ open: true, message: 'Ayah updated successfully!' });
      loadData(); // reload
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to update Ayah' });
    }
  };

  if (loading) {
    return (
      <AdminLayout title={`Surah ${id} Details`}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Surah: ${surah?.englishName || id}`}>
      <Box mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/quran')} color="textSecondary">
          Back to List
        </Button>
      </Box>

      {surah && (
        <Paper className={classes.headerCard} elevation={0}>
          <Typography variant="h6" gutterBottom style={{ color: '#38bdf8' }}>
            Surah Metadata ({surah.name})
          </Typography>
          <Grid container spacing={3} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4, fontWeight: 600 }}>
                Surah Image (Firebase Storage)
              </Typography>
              <ImageUploadBox
                value={surah.surahImage || ''}
                onChange={(url) => setSurah({ ...surah, surahImage: url })}
                folder="surah_images"
                label="Upload Surah Cover"
                helperText="Upload image to Firebase Storage for this Surah"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Transliteration (English)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={surah.transliterationEn || ''}
                onChange={(e) => handleMetaChange('transliterationEn', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Transliteration (Bangla)"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.textField}
                value={surah.transliterationBn || ''}
                onChange={(e) => handleMetaChange('transliterationBn', e.target.value)}
              />
            </Grid>
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={saveSurahMeta}
              disabled={savingMeta}
              disableElevation
            >
              Save Metadata
            </Button>
          </Box>
        </Paper>
      )}

      <Typography variant="h6" gutterBottom style={{ color: '#f8fafc', marginTop: 32 }}>
        Ayahs ({surah?.numberOfAyahs})
      </Typography>

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="60">Ayah</TableCell>
              <TableCell width="40%">Arabic</TableCell>
              <TableCell>Audio Sources</TableCell>
              <TableCell>Translations Count</TableCell>
              <TableCell>Tafsir Count</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ayahs.map((ayah) => (
              <TableRow key={ayah.id} hover>
                <TableCell>
                  <Typography color="textSecondary" style={{ fontWeight: 600 }}>{ayah.numberInSurah}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color="textPrimary" style={{ fontFamily: 'sans-serif', fontSize: '1.2rem', textAlign: 'right' }} dir="rtl">
                    {ayah.text}
                  </Typography>
                </TableCell>
                <TableCell>
                  {ayah.audio && ayah.audio.length > 0 ? (
                    <Box>
                      {ayah.audio.map((a, i) => (
                        <Typography key={i} color="textSecondary" style={{ fontSize: '0.85rem' }}>
                          {a.source || 'Unknown'}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography style={{ color: theme.palette.text.secondary }}>—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography color="textSecondary">
                    {ayah.translations?.length || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color="textSecondary">
                    {ayah.tafsirs?.length || 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton className={classes.actionBtn} onClick={() => handleEditAyah(ayah)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {editAyahOpen && selectedAyah && (
        <AyahEditDialog
          open={editAyahOpen}
          ayah={selectedAyah}
          onClose={() => setEditAyahOpen(false)}
          onSave={handleSaveAyah}
        />
      )}

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
